import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationItem from './NotificationItem';

const MAX_IN_DROPDOWN = 10;

interface NotificationBellProps {
  /** When set, render as a labelled nav item (icon + label) instead of an
   *  icon-only button — used by the desktop primary nav so the bell matches the
   *  other "icon + label" links. */
  label?: string;
  /** Icon size; defaults to the icon-only (mobile) size. */
  iconClassName?: string;
}

const NotificationBell = ({ label, iconClassName = 'h-5 w-5' }: NotificationBellProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const {
    notifications, unseenCount, markSeen, markAllSeen,
    respondInvite, respondedLinkIds, respondProfileInfo, respondedRequestIds,
  } = useNotifications();

  // Opening the panel auto-marks everything seen (clears the badge), which would instantly drop the
  // per-row "new" dots — too fast to tell what just arrived. So we snapshot the ids that were unseen
  // at open time and keep THEM visually lit for a few seconds, independent of the seen flag.
  const [highlightIds, setHighlightIds] = useState<Set<string>>(() => new Set());
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (highlightTimer.current) clearTimeout(highlightTimer.current); }, []);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Opening the panel marks everything as seen (seen ≠ acted upon).
    if (next && unseenCount > 0) {
      setHighlightIds(new Set(notifications.filter((n) => n.status === 'unseen').map((n) => n.id)));
      markAllSeen();
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => setHighlightIds(new Set()), 10_000);
    }
  };

  const recent = notifications.slice(0, MAX_IN_DROPDOWN);

  const iconWithBadge = (
    <span className="relative inline-flex">
      <Bell className={iconClassName} />
      {unseenCount > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
          {unseenCount > 9 ? '9+' : unseenCount}
        </span>
      )}
    </span>
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {label ? (
          <button
            type="button"
            className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            aria-label={t('notifications.aria_open')}
          >
            {iconWithBadge}
            {label}
          </button>
        ) : (
          <button
            type="button"
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground hover:bg-accent"
            aria-label={t('notifications.aria_open')}
          >
            {iconWithBadge}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-3 py-2 text-sm font-semibold text-foreground">
          {t('notifications.title')}
        </div>
        <Separator />
        {recent.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t('notifications.empty')}
          </p>
        ) : (
          <ScrollArea className="max-h-80">
            <div className="divide-y divide-border">
              {recent.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  highlighted={highlightIds.has(n.id)}
                  // Tapping a notification (navigating rows + the fallback) closes the dropdown too —
                  // the inline Accept/Reject buttons don't call onActivate, so they leave it open.
                  onActivate={(id) => { markSeen(id); setOpen(false); }}
                  onRespondInvite={respondInvite}
                  onRespondProfileInfo={respondProfileInfo}
                  responded={
                    n.type === 'profile_info_requested'
                      ? respondedRequestIds.has(String((n.payload as { request_id?: string })?.request_id ?? ''))
                      : respondedLinkIds.has(String((n.payload as { linkId?: string })?.linkId ?? ''))
                  }
                />
              ))}
            </div>
          </ScrollArea>
        )}
        <Separator />
        <Link
          to="/notifications"
          className="block px-3 py-2 text-center text-sm font-medium text-primary hover:underline"
          onClick={() => setOpen(false)}
        >
          {t('notifications.view_all')}
        </Link>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
